/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Testimonials section for Face Swap SEO page
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'Game-Changer for Content Creation',
    content:
      'The AI face swap tool completely changed how I make short videos. Before, I spent hours editing, but now I can produce realistic swaps in just minutes. My audience engagement doubled, and clients are amazed by the quality.',
    role: 'Social Media Influencer',
    name: 'Emily Johnson',
  },
  {
    title: 'Perfect for Teaching & Education',
    content:
      'As a high school history teacher, I use the face swap feature to appear as historical figures in my lectures. My students find it both fun and educational \u2014 it makes lessons far more engaging than traditional slides.',
    role: 'History Teacher',
    name: 'Michael Lee',
  },
  {
    title: 'Professional Results Without the Cost',
    content:
      'I often work on marketing campaigns for small businesses. This tool lets me create ad videos with celebrity-style swaps without hiring actors or expensive studios. It looks professional and saves huge production costs.',
    role: 'Marketing Consultant',
    name: 'Sophia Martinez',
  },
  {
    title: 'Unbelievably Realistic',
    content:
      "I've tried many face swap apps, but none looked natural when people moved. This platform handles angles, lighting, and expressions seamlessly. My TikTok followers often think the videos are real!",
    role: 'TikTok Creator',
    name: 'David Kim',
  },
  {
    title: 'Fast, Fun, and Reliable',
    content:
      "I'm a freelance video editor, and this tool became part of my daily workflow. It's so easy to use, supports multiple formats, and delivers fast results. Honestly, it performs like software that usually costs hundreds of dollars.",
    role: 'Independent Video Editor',
    name: 'Hannah Williams',
  },
  {
    title: 'Made My Dance Content Go Viral',
    content:
      'I create dance videos for Instagram and TikTok, and adding face swaps has taken my content to the next level. Now I can perform as different celebrities or even anime characters while keeping my own dance moves. My latest clip hit 2M views in just a week \u2014 fans loved the creativity!',
    role: 'Influencer',
    name: 'Jessica Tan',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{name}</p>
        <p className="text-gray-500 text-sm">{role}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          Trusted by 100,000+ Teams Worldwide
        </h2>

        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
